//@import dependencies
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
//@end

//@import components
import AppContainer from 'app_components/layouts/app'
import BreadcrumbComponent from 'app_components/commons/breadcrumb'
import DataTableComponent from 'app_components/commons/dataTable'
import CourseForm from 'app_components/modules/admin/course'
import CourseSkeleton from './courseSkeleton'
import CourseScreenFormFilterLayout from './courseScreenFormFilterLayout'
//@end

// @import hooks
// @end

// @INFO Import types
import { IPagination } from 'app_types/pagination/paginationTypes'
import { ICourseListItem } from 'app_types/admin/course/courseTypes'
// @end

// @INFO Import services
// @end

// @INFO Import Styles
import './../courseScreen.scss'
import { BsPlusLg } from 'react-icons/bs'
import DropDownComponent from 'app_components/commons/DropDownComponent/DropDownComponent'
import TermService from 'app_services/admin/term/termService'
import { ITerm, Terms } from 'app_types/admin/term/term.types'
import { routingService } from 'app_services/routingService'

// @ADD Component types
interface ICourseScreenLayoutProps {
  itemList: ICourseListItem[]
  status: null | 'loading' | 'error'
  add: boolean
  edit: boolean
  view: boolean
  editData: any
  selectedRow: null | string
  pagination: IPagination
  addItem: any
  updateItem: any
  showAddForm: (add: boolean) => void
  showEditForm: (edit: boolean, editData?: any) => void
  onViewerRow: (view: boolean, editData?: any) => void
  onPageChange: (page: number) => void
  onRowClick: (row: any) => void
  removeItem: (userId: string) => void
  onSearch: () => void
  searchFormInputs?: any
}

const CourseScreenLayout: React.FC<ICourseScreenLayoutProps> = (props) => {

  const { t } = useTranslation()
  const searchForm = useForm()
  const [filterCategories, setFilterCategories] = useState<ITerm<{ typeRelated: Terms }>[]>([])
  const termService = useMemo(() => new TermService(), [])

  const fetchFilterCategories = useCallback(async () => {
    const response = await termService.list({ type: Terms.COURSE_FILTER_CATEGORY, enabled: true, nPerPage: '100' })
    if (response?.code === 200 && response?.terms?.length) {
      setFilterCategories(response.terms)
    }
  }, [termService])

  useEffect(() => {
    fetchFilterCategories()
  }, [fetchFilterCategories])

  return(
    <AppContainer>

      <div className='course-component container-fluid'>
        <BreadcrumbComponent links={['#']} texts={[`${t('components.breadcrumb.courses')}`]} />

        <div className='course-screen'>

          {props.status === 'loading' && (
            <CourseSkeleton />
          )}

          {props.status !== 'loading' && !props.add && !props.edit && !props.view && (
            <>
              <h2>{t('screens.courses.title')}</h2>

              <div className="mb-3">
                <CourseScreenFormFilterLayout
                  searchForm={searchForm}
                  searchFormInputs={props.searchFormInputs}
                  onSearch={props.onSearch}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0px 20px', gap: '16px' }}>
                <DropDownComponent title='Categorías de filtros'>
                  {filterCategories?.map((term) => (
                    <DropDownComponent.Item
                      key={term._id}
                      href={routingService.getSecurePath(`/admin/courses/filter/${term._id}`)}
                      as='a'
                      target='_blank'
                    >
                      {term.name}
                    </DropDownComponent.Item>
                  ))}
                </DropDownComponent>
                <BsPlusLg cursor='pointer' size={18} onClick={() => props.showAddForm(true)} />
              </div>
              <DataTableComponent
                data={props.itemList}
                headerKeys={[
                  `${t('components.dataTable.headers.course.schedulingCode')}`,
                  `${t('components.dataTable.headers.course.schedulingMode')}`,
                  `${t('components.dataTable.headers.course.program')}`,
                ]}
                keys={['program.code', 'schedulingMode.name', 'program.name']}
                selectedRow={props.selectedRow}
                pagination={props.pagination}
                detailsKey={[]}
                onClickRow={props.onRowClick}
                onRemoveRow={props.removeItem}
                onPageChange={props.onPageChange}
                onEditRow={props.showEditForm}
                onViewerRow={props.onViewerRow}
                onModal={null}
              />
            </>
          )}

          {props.add && (
            <div className="course-container-form col-6 mb-3">
              <div className="d-flex align-items-center mb-2">
                <h2 className="form-title">
                  {t("screens.courses.add_layout.title_page")}
                </h2>
                <button className="btn btn-light btn-sm border ml-2" onClick={() => props.showAddForm(false)}>
                  {t("buttons.back")}
                </button>
              </div>
              <CourseForm
                onHandleSubmit={props.addItem}
                viewer={false}
              />
            </div>
          )}

          {(props.edit || props.view) && props.editData && (
            <div className="course-container-form col-6 mb-3">
              <div className="d-flex align-items-center mb-2">
                {props.edit && (
                  <>
                    <h2 className="form-title">
                      {t("screens.courses.edit_layout.title_page")}
                    </h2>
                    <button className="btn btn-light btn-sm border ml-2" onClick={() => props.showEditForm(false)}>
                      {t("buttons.back")}
                    </button>
                  </>
                )}
                {props.view && (
                  <>
                    <h2 className="form-title">
                      {t("screens.courses.view_layout.title_page")}
                    </h2>
                    <button className="btn btn-light btn-sm border ml-2" onClick={() => props.onViewerRow(false)}>
                      {t("buttons.back")}
                    </button>
                  </>
                )}
              </div>
              <CourseForm
                editData={props.editData}
                onHandleSubmit={props.updateItem}
                viewer={props.view}
              />
            </div>
          )}
        </div>
      </div>

    </AppContainer>
  )
}

export default CourseScreenLayout
